import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/msalConfig';
import {
  fetchSurveys,
  fetchReminders,
  rescheduleReminder,
  cancelReminder,
  type SurveyListItem,
  type ReminderDto,
} from '../../api/surveyApi';
import { fetchEmployees, fetchDeliveryPreview, deliverSurvey } from '../../api/employeeApi';

function ReminderRow({
  surveyId,
  reminder,
  onUpdated,
  onCancelled,
  getToken,
}: {
  surveyId: string;
  reminder: ReminderDto;
  onUpdated: (r: ReminderDto) => void;
  onCancelled: (id: string) => void;
  getToken: () => Promise<string>;
}) {
  const [editing, setEditing] = useState(false);
  const [newDate, setNewDate] = useState(reminder.scheduledAt.slice(0, 16));
  const [busy, setBusy] = useState(false);

  const sent = !!reminder.sentAt;
  const cancelled = reminder.isCancelled;

  const handleReschedule = async () => {
    setBusy(true);
    try {
      const token = await getToken();
      const updated = await rescheduleReminder(surveyId, reminder.id, new Date(newDate).toISOString(), token);
      onUpdated(updated);
      setEditing(false);
    } catch { /* show nothing — transient */ } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Cancel this reminder?')) return;
    setBusy(true);
    try {
      const token = await getToken();
      await cancelReminder(surveyId, reminder.id, token);
      onCancelled(reminder.id);
    } catch { } finally {
      setBusy(false);
    }
  };

  const statusBadge = sent
    ? <span style={badge('#dcfce7', '#166534')}>Sent</span>
    : cancelled
    ? <span style={badge('#f3f4f6', '#6b7280')}>Cancelled</span>
    : <span style={badge('#fef9c3', '#854d0e')}>Pending</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
      {editing ? (
        <>
          <input
            type="datetime-local"
            value={newDate}
            onChange={e => setNewDate(e.target.value)}
            style={{ padding: '0.35rem 0.5rem', fontSize: '0.875rem', border: '1px solid var(--color-border)', borderRadius: 4 }}
          />
          <button className="btn btn-primary" style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem' }} disabled={busy} onClick={handleReschedule}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-ghost" style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem' }} onClick={() => setEditing(false)}>
            Cancel
          </button>
        </>
      ) : (
        <>
          <span style={{ fontSize: '0.875rem', flex: 1, minWidth: 160 }}>
            {new Date(reminder.scheduledAt).toLocaleString()}
          </span>
          {statusBadge}
          {!sent && !cancelled && (
            <>
              <button className="btn btn-ghost" style={{ minHeight: 28, padding: '0 0.6rem', fontSize: '0.8rem' }}
                onClick={() => setEditing(true)}>Reschedule</button>
              <button className="btn btn-ghost" style={{ minHeight: 28, padding: '0 0.6rem', fontSize: '0.8rem', color: 'var(--color-error)' }}
                onClick={handleCancel} disabled={busy}>Cancel</button>
            </>
          )}
        </>
      )}
    </div>
  );
}

function SendPanel({
  survey,
  getToken,
  onSent,
}: {
  survey: SurveyListItem;
  getToken: () => Promise<string>;
  onSent: () => void;
}) {
  const [state, setState] = useState<'idle' | 'loading' | 'preview' | 'sending' | 'done' | 'error'>('idle');
  const [preview, setPreview] = useState<{ total: number; byChannel: { channel: string; count: number }[] } | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const load = async () => {
    setState('loading');
    try {
      const token = await getToken();
      const employees = await fetchEmployees(token, { activeOnly: true });
      const ids = employees.map(e => e.id);
      const p = await fetchDeliveryPreview(token, survey.id, ids);
      setPreview({ total: p.totalEmployees, byChannel: p.byChannel });
      setState('preview');
    } catch {
      setErrorMsg('Could not load employee list.');
      setState('error');
    }
  };

  const send = async () => {
    if (!preview || !survey.closedAt) return;
    setState('sending');
    try {
      const token = await getToken();
      const employees = await fetchEmployees(token, { activeOnly: true });
      const ids = employees.map(e => e.id);
      await deliverSurvey(token, survey.id, ids, survey.closedAt, []);
      setState('done');
      setTimeout(onSent, 1200);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Send failed.');
      setState('error');
    }
  };

  if (state === 'idle') {
    return (
      <button className="btn btn-primary" style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem' }}
        onClick={load}>
        Send to employees
      </button>
    );
  }

  return (
    <div style={{ borderTop: '1px solid var(--color-border)', padding: '0.75rem 1rem', background: '#fafafa' }}>
      {state === 'loading' && <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: 0 }}>Loading employee list…</p>}

      {state === 'preview' && preview && (
        <>
          <p style={{ fontSize: '0.875rem', margin: '0 0 0.5rem' }}>
            <strong>{preview.total} employee{preview.total !== 1 ? 's' : ''}</strong> will receive this survey:
          </p>
          <ul style={{ margin: '0 0 0.75rem', paddingLeft: '1.25rem', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
            {preview.byChannel.map(c => (
              <li key={c.channel}>{c.count} via {c.channel}</li>
            ))}
          </ul>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary" style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem' }}
              onClick={send}>
              Confirm & send
            </button>
            <button className="btn btn-ghost" style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem' }}
              onClick={() => setState('idle')}>
              Cancel
            </button>
          </div>
        </>
      )}

      {state === 'sending' && <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: 0 }}>Sending…</p>}

      {state === 'done' && (
        <p style={{ fontSize: '0.875rem', color: '#166534', margin: 0 }}>Sent! Survey is now live.</p>
      )}

      {state === 'error' && (
        <p style={{ fontSize: '0.875rem', color: 'var(--color-error)', margin: 0 }}>{errorMsg}</p>
      )}
    </div>
  );
}

function SurveyCard({ survey, getToken, onChanged }: { survey: SurveyListItem; getToken: () => Promise<string>; onChanged: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [reminders, setReminders] = useState<ReminderDto[]>([]);
  const [loadingReminders, setLoadingReminders] = useState(false);

  const handleExpand = async () => {
    if (!expanded && reminders.length === 0) {
      setLoadingReminders(true);
      try {
        const token = await getToken();
        const r = await fetchReminders(survey.id, token);
        setReminders(r);
      } finally {
        setLoadingReminders(false);
      }
    }
    setExpanded(e => !e);
  };

  const isDraft = !survey.isActive && !survey.isImported && !survey.closedAt;

  const statusLabel = survey.isImported
    ? 'Imported'
    : survey.isActive
    ? 'Live'
    : isDraft
    ? 'Draft'
    : 'Closed';

  const statusColor = survey.isActive ? '#166534' : survey.isImported ? '#1e40af' : isDraft ? '#854d0e' : '#6b7280';
  const statusBg    = survey.isActive ? '#dcfce7' : survey.isImported ? '#dbeafe' : isDraft ? '#fef9c3' : '#f3f4f6';

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, marginBottom: '0.75rem', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '0.95rem' }}>{survey.importLabel ?? survey.title}</strong>
            <span style={badge(statusBg, statusColor)}>{statusLabel}</span>
          </div>
          {survey.startsAt && (
            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
              Started {new Date(survey.startsAt).toLocaleDateString()}
              {survey.pendingReminders > 0 && (
                <span style={{ marginLeft: '0.5rem', color: 'var(--color-primary)' }}>
                  · {survey.pendingReminders} reminder{survey.pendingReminders !== 1 ? 's' : ''} pending
                </span>
              )}
            </div>
          )}
        </div>

        {survey.isActive && (
          <button
            className="btn btn-ghost"
            style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem' }}
            onClick={handleExpand}
          >
            {expanded ? 'Hide reminders' : 'Manage reminders'}
          </button>
        )}
      </div>

      {survey.isActive && expanded && (
        <div style={{ borderTop: '1px solid var(--color-border)', padding: '0.75rem 1rem', background: '#fafafa' }}>
          <strong style={{ fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>Reminders</strong>
          {loadingReminders && <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>Loading…</p>}
          {!loadingReminders && reminders.length === 0 && (
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>No reminders configured for this survey.</p>
          )}
          {reminders.map(r => (
            <ReminderRow
              key={r.id}
              surveyId={survey.id}
              reminder={r}
              getToken={getToken}
              onUpdated={updated => setReminders(prev => prev.map(x => x.id === updated.id ? updated : x))}
              onCancelled={id => setReminders(prev => prev.map(x => x.id === id ? { ...x, isCancelled: true } : x))}
            />
          ))}
        </div>
      )}

      {isDraft && <SendPanel survey={survey} getToken={getToken} onSent={onChanged} />}
    </div>
  );
}

export function SurveysList() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<SurveyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getToken = async () => {
    const r = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return r.accessToken;
  };

  const load = () => {
    setLoading(true);
    getToken()
      .then(t => fetchSurveys(t))
      .then(setSurveys)
      .catch(() => setError('Could not load surveys.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>;
  if (error)   return <p style={{ color: 'var(--color-error)' }}>{error}</p>;

  const drafts   = surveys.filter(s => !s.isActive && !s.isImported && !s.closedAt);
  const live     = surveys.filter(s => s.isActive);
  const archived = surveys.filter(s => !s.isActive && (s.isImported || !!s.closedAt));

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Surveys</h2>
        <button className="btn btn-primary" style={{ minHeight: 36, padding: '0 1rem', fontSize: '0.875rem' }}
          onClick={() => navigate('/admin/surveys/new')}>
          + New Survey
        </button>
      </div>

      {drafts.length > 0 && (
        <>
          <h3 style={sectionHead}>Drafts</h3>
          {drafts.map(s => <SurveyCard key={s.id} survey={s} getToken={getToken} onChanged={load} />)}
        </>
      )}

      {live.length > 0 && (
        <>
          <h3 style={{ ...sectionHead, marginTop: drafts.length > 0 ? '1.25rem' : 0 }}>Live</h3>
          {live.map(s => <SurveyCard key={s.id} survey={s} getToken={getToken} onChanged={load} />)}
        </>
      )}

      {archived.length > 0 && (
        <>
          <h3 style={{ ...sectionHead, marginTop: (drafts.length > 0 || live.length > 0) ? '1.25rem' : 0 }}>Completed / Imported</h3>
          {archived.map(s => <SurveyCard key={s.id} survey={s} getToken={getToken} onChanged={load} />)}
        </>
      )}

      {surveys.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)', padding: '2rem 0', textAlign: 'center' }}>No surveys yet.</p>
      )}
    </div>
  );
}

const sectionHead: React.CSSProperties = {
  fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em',
  color: 'var(--color-text-muted)', margin: '0 0 0.5rem',
};

function badge(bg: string, color: string): React.CSSProperties {
  return {
    display: 'inline-block', background: bg, color, borderRadius: 12,
    padding: '0.15rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap',
  };
}
